// File: frontend/src/components/admin/AdminClubs.tsx
// src/components/admin/AdminClubs.tsx

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaPlus,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaToggleOn,
  FaToggleOff,
  FaGlobe,
  FaLock,
  FaUnlock,
  FaInfoCircle,
} from "react-icons/fa";

import { ClubModel } from "../../models/ClubModel";
import {
  AdminClubFilters,
  getAdminClubs,
  createAdminClub,
  updateAdminClub,
  deleteAdminClub,
  toggleClubActive,
  setClubLocked,
} from "../../services/adminClubService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import { ConfirmModal } from "../common/ConfirmModal";
import { LockReasonFields } from "./common/LockReasonFields";
import { LockInfoModal } from "./common/LockInfoModal";
import {
  ADMIN_PAGE_SIZE,
  ADMIN_CLUB_SHORT_NAME_MAX_LENGTH,
  ADMIN_CLUB_NAME_MAX_LENGTH,
  ADMIN_CLUB_LONG_NAME_MAX_LENGTH,
  ADMIN_CLUB_ABBREVIATION_MAX_LENGTH,
  ADMIN_CITY_MAX_LENGTH,
  ADMIN_CLUB_COLOR_MAX_LENGTH,
  ADMIN_URL_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { InputClearButton } from "../common/InputClearButton";
import { AdminCharCounter, AdminFieldInfoTooltip } from "./common";
import {
  MdCountryDto,
  getPublicCountries,
} from "../../services/masterDataService";
import "./AdminClubs.css";

interface AdminClubsProps {
  isAdmin: boolean;
}

export const AdminClubs: React.FC<AdminClubsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // Content for the (i) tooltip in the create/edit modal header — lists where
  // each club field ends up being shown across the site, so the admin can
  // predict the effect of a change before saving it.
  const clubFieldsInfoItems = [
    t("admin_club_fields_info_item_1"),
    t("admin_club_fields_info_item_2"),
    t("admin_club_fields_info_item_3"),
    t("admin_club_fields_info_item_4"),
    t("admin_club_fields_info_item_5"),
    t("admin_club_fields_info_item_6"),
    t("admin_club_fields_info_item_7"),
    t("admin_club_fields_info_item_8"),
  ];

  // Countries loaded from master data API — replaces hardcoded COUNTRY_OPTIONS
  const [countries, setCountries] = useState<MdCountryDto[]>([]);
  useEffect(() => {
    getPublicCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  // ── Data state ────────────────────────────────────────────────
  const [clubs, setClubs] = useState<ClubModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // ── Lock modal state (shown only when locking, not unlocking) ──
  const [lockTarget, setLockTarget] = useState<ClubModel | null>(null);
  const [lockReasonDraft, setLockReasonDraft] = useState("");
  const [lockReasonVisibleDraft, setLockReasonVisibleDraft] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockInfoTarget, setLockInfoTarget] = useState<ClubModel | null>(null);

  // ── Pagination state ──────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // ── Filter state ──────────────────────────────────────────────
  const [filters, setFilters] = useState<AdminClubFilters>({
    search: "",
    country: "",
    active: "",
    locked: "",
    sortBy: "shortName",
    sortDir: "asc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // ── Modal state ───────────────────────────────────────────────
  const [modalType, setModalType] = useState<
    "create" | "edit" | "delete" | null
  >(null);
  const [selectedClub, setSelectedClub] = useState<ClubModel | null>(null);

  // ── Form state ────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    shortName: "",
    nameTr: "",
    nameEn: "",
    longName: "",
    abbreviation: "",
    logoUrl: "",
    color1: "",
    color2: "",
    color3: "",
    country: "TR",
    city: "",
    foundedYear: "" as string | number,
    active: true,
  });
  const [modalError, setModalError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // ── Fetch clubs ───────────────────────────────────────────────
  const fetchClubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminClubs(
        currentPage,
        ADMIN_PAGE_SIZE,
        filters,
      );
      setClubs(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_club_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  // ── Toolbar handlers ──────────────────────────────────────────
  const handleSearch = () => {
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: searchInput }));
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setCurrentPage(0);
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      country: "",
      active: "",
      locked: "",
      sortBy: "shortName",
      sortDir: "asc",
    });
    setSearchInput("");
    setCurrentPage(0);
  };

  const hasActiveFilters =
    !!filters.search || !!filters.country || filters.active !== "" || filters.locked !== "";

  const handlePaginate = (page: number) => setCurrentPage(page - 1);

  // ── Modal helpers ─────────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      shortName: "",
      nameTr: "",
      nameEn: "",
      longName: "",
      abbreviation: "",
      logoUrl: "",
      color1: "",
      color2: "",
      color3: "",
      country: "TR",
      city: "",
      foundedYear: "",
      active: true,
    });
    setModalError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalType("create");
  };

  const openEditModal = (club: ClubModel) => {
    setSelectedClub(club);
    setFormData({
      shortName: club.shortName,
      nameTr: club.nameTr ?? "",
      nameEn: club.nameEn ?? "",
      longName: club.longName ?? "",
      abbreviation: club.abbreviation ?? "",
      logoUrl: club.logoUrl ?? "",
      color1: club.color1 ?? "",
      color2: club.color2 ?? "",
      color3: club.color3 ?? "",
      country: club.country,
      city: club.city ?? "",
      foundedYear: club.foundedYear ?? "",
      active: club.active,
    });
    setModalError(null);
    setModalType("edit");
  };

  const openDeleteModal = (club: ClubModel) => {
    setSelectedClub(club);
    setModalError(null);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedClub(null);
    setModalError(null);
  };

  // ── CRUD handlers ─────────────────────────────────────────────
  const handleCreate = async () => {
    if (!formData.shortName.trim()) {
      setModalError(t("admin_club_shortname_required"));
      return;
    }
    if (!formData.country.trim()) {
      setModalError(t("admin_club_country_required"));
      return;
    }
    try {
      await createAdminClub({
        shortName: formData.shortName.trim(),
        nameTr: formData.nameTr.trim() || undefined,
        nameEn: formData.nameEn.trim() || undefined,
        longName: formData.longName.trim() || undefined,
        abbreviation: formData.abbreviation.trim() || undefined,
        logoUrl: formData.logoUrl.trim() || undefined,
        color1: formData.color1.trim() || undefined,
        color2: formData.color2.trim() || undefined,
        color3: formData.color3.trim() || undefined,
        country: formData.country.toUpperCase(),
        city: formData.city.trim() || undefined,
        foundedYear: formData.foundedYear
          ? Number(formData.foundedYear)
          : undefined,
      });
      setSuccessMessage(t("admin_club_created"));
      closeModal();
      fetchClubs();
    } catch (err: any) {
      setModalError(err.message || t("admin_club_create_error"));
    }
  };

  const handleUpdate = async () => {
    if (!selectedClub) return;
    if (!formData.shortName.trim()) {
      setModalError(t("admin_club_shortname_required"));
      return;
    }
    try {
      await updateAdminClub(selectedClub.id, {
        shortName: formData.shortName.trim(),
        nameTr: formData.nameTr.trim() || undefined,
        nameEn: formData.nameEn.trim() || undefined,
        longName: formData.longName.trim() || undefined,
        abbreviation: formData.abbreviation.trim() || undefined,
        logoUrl: formData.logoUrl.trim() || undefined,
        // Pass "" to clear a color, pass the value to set it, pass undefined to leave unchanged
        // Here we always send the current form value (including empty string to clear)
        color1: formData.color1.trim(),
        color2: formData.color2.trim(),
        color3: formData.color3.trim(),
        country: formData.country.toUpperCase(),
        city: formData.city.trim() || undefined,
        foundedYear: formData.foundedYear
          ? Number(formData.foundedYear)
          : undefined,
        active: formData.active,
      });
      setSuccessMessage(t("admin_club_updated"));
      closeModal();
      fetchClubs();
    } catch (err: any) {
      setModalError(err.message || t("admin_club_update_error"));
    }
  };

  const handleDelete = async () => {
    if (!selectedClub) return;
    try {
      await deleteAdminClub(selectedClub.id);
      setSuccessMessage(t("admin_club_deleted"));
      closeModal();
      fetchClubs();
    } catch (err: any) {
      const msg: string = err.message || "";
      if (msg === "CLUB_HAS_MATCHES") {
        setModalError(t("admin_club_delete_has_matches_error"));
      } else {
        setModalError(msg || t("admin_club_delete_error"));
      }
    }
  };

  // Quick-toggle active status directly from the table row
  const handleToggleActive = async (club: ClubModel) => {
    try {
      await toggleClubActive(club.id, !club.active);
      setSuccessMessage(
        !club.active
          ? t("admin_toggle_activated", { name: club.shortName })
          : t("admin_toggle_deactivated", { name: club.shortName }),
      );
      fetchClubs();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  const handleToggleLock = async (club: ClubModel) => {
    if (!club.isLocked) {
      // About to lock — collect an optional reason first
      setLockTarget(club);
      setLockReasonDraft("");
      setLockReasonVisibleDraft(false);
      return;
    }
    try {
      await setClubLocked(club.id, false);
      setSuccessMessage(t("admin_topic_unlocked", { name: club.shortName }));
      fetchClubs();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  const handleConfirmLock = async () => {
    if (!lockTarget) return;
    setLockBusy(true);
    try {
      await setClubLocked(lockTarget.id, true, lockReasonDraft.trim() || undefined, lockReasonVisibleDraft);
      setSuccessMessage(t("admin_topic_locked", { name: lockTarget.shortName }));
      setLockTarget(null);
      fetchClubs();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    } finally {
      setLockBusy(false);
    }
  };

  const handleCancelLock = () => {
    setLockTarget(null);
    setLockReasonDraft("");
    setLockReasonVisibleDraft(false);
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="admin-clubs">
      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_club_search_placeholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {t("admin_filters")}
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        <button className="create-club-btn" onClick={openCreateModal}>
          <FaPlus /> {t("admin_club_add")}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_country")}</label>
            <select
              value={filters.country}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, country: e.target.value }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all_countries")}</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.displayName}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_status_label")}</label>
            <select
              value={filters.active}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  active: e.target.value as AdminClubFilters["active"],
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_active")}</option>
              <option value="false">{t("admin_inactive")}</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_ct_lock_status")}</label>
            <select
              value={filters.locked}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  locked: e.target.value as AdminClubFilters["locked"],
                }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_ct_locked")}</option>
              <option value="false">{t("admin_ct_unlocked")}</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="clubs-table-container">
        {loading ? (
          <div className="clubs-loading">{t("loading")}</div>
        ) : error && clubs.length === 0 ? (
          <AdminErrorBanner message={error} />
        ) : clubs.length === 0 ? (
          <div className="clubs-empty">{t("admin_club_not_found")}</div>
        ) : (
          <table className="clubs-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>ID {getSortIcon("id")}</th>
                <th className="col-logo">Logo</th>
                <th onClick={() => handleSort("shortName")}>
                  {t("admin_club_col_short_name")} {getSortIcon("shortName")}
                </th>
                <th className="col-longname">
                  {t("admin_club_col_long_name")}
                </th>
                <th className="col-abbr">{t("admin_club_col_abbreviation")}</th>
                <th className="col-colors">{t("admin_club_col_colors")}</th>
                <th
                  onClick={() => handleSort("country")}
                  className="col-country"
                >
                  {t("admin_country")} {getSortIcon("country")}
                </th>
                <th className="col-city">{t("admin_city")}</th>
                <th
                  onClick={() => handleSort("foundedYear")}
                  className="col-founded"
                >
                  {t("admin_club_col_founded")} {getSortIcon("foundedYear")}
                </th>
                <th className="col-teams">{t("admin_club_col_teams")}</th>
                <th onClick={() => handleSort("active")} className="col-active">
                  {t("admin_status_label")} {getSortIcon("active")}
                </th>
                <th>{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {clubs.map((club) => (
                <tr
                  key={club.id}
                  className={!club.active ? "row-inactive" : ""}
                >
                  <td className="club-id">#{club.id}</td>

                  {/* Dedicated logo column */}
                  <td className="col-logo">
                    {club.logoUrl ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${club.logoUrl}`}
                        alt={club.shortName}
                        className="club-logo-thumb"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="club-logo-placeholder">
                        <FaBuilding />
                      </div>
                    )}
                  </td>

                  <td className="club-shortname">
                    {/* Link to the club page — same pattern as AdminPeople */}
                    <Link
                      to={`/kulup/${club.slug}`}
                      style={{ color: "inherit", textDecoration: "none" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.textDecoration = "none")
                      }
                    >
                      <span className="club-name-text">{club.shortName}</span>
                    </Link>
                  </td>

                  <td className="col-longname">
                    <span className="text-muted-cell">
                      {club.longName ?? "—"}
                    </span>
                  </td>

                  <td className="col-abbr">
                    {club.abbreviation ? (
                      <span className="club-abbr-badge">
                        {club.abbreviation}
                      </span>
                    ) : (
                      <span className="text-muted-cell">—</span>
                    )}
                  </td>

                  {/* Color swatches — shows up to 3 colored circles */}
                  <td className="col-colors">
                    <div className="club-color-swatches">
                      {[club.color1, club.color2, club.color3].map(
                        (color, i) =>
                          color ? (
                            <span
                              key={i}
                              className="club-color-swatch"
                              style={{ backgroundColor: color }}
                              title={color}
                            />
                          ) : null,
                      )}
                      {!club.color1 && !club.color2 && !club.color3 && (
                        <span className="text-muted-cell">—</span>
                      )}
                    </div>
                  </td>

                  <td className="col-country">
                    <span className="club-country-badge">
                      <FaGlobe className="country-icon" />
                      {club.country}
                    </span>
                  </td>

                  <td className="col-city">
                    <span className="text-muted-cell">{club.city ?? "—"}</span>
                  </td>

                  <td className="col-founded">
                    <span className="text-muted-cell">
                      {club.foundedYear ?? "—"}
                    </span>
                  </td>

                  <td className="col-teams">
                    <span className="team-count-badge">
                      {club.teams?.length ?? 0}
                    </span>
                  </td>

                  <td className="col-active">
                    {/* Quick-toggle active directly from row */}
                    <button
                      className={`active-toggle-btn ${club.active ? "is-active" : "is-inactive"}`}
                      title={
                        club.active
                          ? t("admin_toggle_active_title")
                          : t("admin_toggle_inactive_title")
                      }
                      onClick={() => handleToggleActive(club)}
                    >
                      {club.active ? (
                        <>
                          <FaToggleOn /> {t("admin_active")}
                        </>
                      ) : (
                        <>
                          <FaToggleOff /> {t("admin_inactive")}
                        </>
                      )}
                    </button>
                  </td>

                  <td className="club-actions">
                    <div className="action-buttons">
                      <button
                        className="action-btn edit"
                        title={t("admin_edit_btn")}
                        onClick={() => openEditModal(club)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className={
                          club.isLocked
                            ? "action-btn lock-active"
                            : "action-btn lock"
                        }
                        title={
                          club.isLocked
                            ? t("admin_topic_unlock")
                            : t("admin_topic_lock")
                        }
                        onClick={() => handleToggleLock(club)}
                      >
                        {club.isLocked ? (
                          <FaUnlock size={12} />
                        ) : (
                          <FaLock size={12} />
                        )}
                      </button>
                      {club.isLocked && (
                        <button
                          className="action-btn"
                          title={t("admin_lock_info_title")}
                          onClick={() => setLockInfoTarget(club)}
                        >
                          <FaInfoCircle size={12} />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          className="action-btn danger"
                          title={t("admin_delete_btn")}
                          onClick={() => openDeleteModal(club)}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="clubs-pagination">
          <span className="page-info">
            {t("admin_club_page_info", {
              current: currentPage + 1,
              total: totalPages,
              items: totalItems,
            })}
          </span>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={handlePaginate}
          />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create / Edit Modal */}
      {(modalType === "create" || modalType === "edit") && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="admin-modal club-form-modal">
            <div className="admin-modal-header">
              <h3>
                {modalType === "create"
                  ? t("admin_club_create_title")
                  : t("admin_club_edit_title")}
                <AdminFieldInfoTooltip title={t("admin_club_fields_info_title")} items={clubFieldsInfoItems} />
              </h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              {/* Row 1: shortName + abbreviation */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_club_short_name_label")} *</label>
                  <input
                    type="text"
                    placeholder={t("admin_club_short_name_placeholder")}
                    maxLength={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH}
                    value={formData.shortName}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, shortName: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.shortName} max={ADMIN_CLUB_SHORT_NAME_MAX_LENGTH} />
                </div>
                <div className="form-group form-group--sm">
                  <label>{t("admin_club_abbreviation_label")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_club_abbreviation_placeholder")}
                    maxLength={ADMIN_CLUB_ABBREVIATION_MAX_LENGTH}
                    value={formData.abbreviation}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        abbreviation: e.target.value.toUpperCase(),
                      }))
                    }
                  />
                  <AdminCharCounter value={formData.abbreviation} max={ADMIN_CLUB_ABBREVIATION_MAX_LENGTH} />
                </div>
              </div>

              {/* Row 2: nameTr + nameEn */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_club_name_tr")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_club_name_tr_placeholder")}
                    maxLength={ADMIN_CLUB_NAME_MAX_LENGTH}
                    value={formData.nameTr}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, nameTr: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.nameTr} max={ADMIN_CLUB_NAME_MAX_LENGTH} />
                </div>
                <div className="form-group">
                  <label>{t("admin_club_name_en")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_club_name_en_placeholder")}
                    maxLength={ADMIN_CLUB_NAME_MAX_LENGTH}
                    value={formData.nameEn}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, nameEn: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.nameEn} max={ADMIN_CLUB_NAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Row 3: longName */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_club_long_name_label")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_club_long_name_placeholder")}
                    maxLength={ADMIN_CLUB_LONG_NAME_MAX_LENGTH}
                    value={formData.longName}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, longName: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.longName} max={ADMIN_CLUB_LONG_NAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Row 4: country + city */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_country")} *</label>
                  <select
                    value={formData.country}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, country: e.target.value }))
                    }
                  >
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} — {c.displayName}
                      </option>
                    ))}
                    <option value="OTHER">{t("admin_other")}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_city")}</label>
                  <input
                    type="text"
                    placeholder={t("admin_city")}
                    maxLength={ADMIN_CITY_MAX_LENGTH}
                    value={formData.city}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, city: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.city} max={ADMIN_CITY_MAX_LENGTH} />
                </div>
                <div className="form-group form-group--sm">
                  <label>{t("admin_club_founded_year")}</label>
                  <input
                    type="number"
                    placeholder="1903"
                    min={1800}
                    max={2100}
                    value={formData.foundedYear}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        foundedYear: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Row 5: logoUrl */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_club_logo_url")}</label>
                  <input
                    type="url"
                    placeholder="https://example.com/logo.png"
                    maxLength={ADMIN_URL_MAX_LENGTH}
                    value={formData.logoUrl}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, logoUrl: e.target.value }))
                    }
                  />
                  <AdminCharCounter value={formData.logoUrl} max={ADMIN_URL_MAX_LENGTH} />
                </div>
              </div>

              {/* Logo preview — only if URL is non-empty */}
              {formData.logoUrl && (
                <div className="club-logo-preview">
                  <img
                    src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${formData.logoUrl}`}
                    alt={t("admin_club_logo_preview")}
                    style={{ cursor: "zoom-in" }}
                    onClick={() =>
                      setLightboxUrl(
                        `${process.env.REACT_APP_API_URL || "http://localhost:8080"}${formData.logoUrl}`,
                      )
                    }
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <span>{t("admin_club_logo_preview")}</span>
                </div>
              )}

              {/* Row 6: club colors — color1, color2, color3
                  Layout: small clickable color swatch (triggers hidden native picker) + hex text input.
                  The native <input type="color"> is hidden (0×0) to avoid it rendering inside the modal.
                  Hex text input is the primary way to enter values. */}
              <div className="form-row">
                {(["color1", "color2", "color3"] as const).map((field, idx) => {
                  const label = [
                    t("admin_club_color1_label"),
                    t("admin_club_color2_label"),
                    t("admin_club_color3_label"),
                  ][idx];
                  const value = formData[field];
                  const pickerId = `color-picker-${field}`;
                  return (
                    <div key={field} className="form-group form-group--color">
                      <label>{label}</label>
                      {value ? (
                        <>
                        <div className="club-color-input-wrapper">
                          {/* Small colored swatch — clicking triggers the hidden native picker */}
                          <div
                            className="club-color-swatch-btn"
                            style={{ backgroundColor: value }}
                            onClick={() =>
                              document.getElementById(pickerId)?.click()
                            }
                            title={t("admin_club_color_pick")}
                          >
                            <input
                              id={pickerId}
                              type="color"
                              value={
                                /^#[0-9A-Fa-f]{6}$/.test(value)
                                  ? value
                                  : "#ffffff"
                              }
                              onChange={(e) =>
                                setFormData((p) => ({
                                  ...p,
                                  [field]: e.target.value,
                                }))
                              }
                              style={{
                                position: "absolute",
                                opacity: 0,
                                width: 0,
                                height: 0,
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                          {/* Hex text input — primary entry method */}
                          <input
                            type="text"
                            className="club-color-hex"
                            placeholder="#RRGGBB"
                            maxLength={ADMIN_CLUB_COLOR_MAX_LENGTH}
                            value={value}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                [field]: e.target.value,
                              }))
                            }
                          />
                          <button
                            type="button"
                            className="club-color-clear"
                            title={t("admin_club_color_clear")}
                            onClick={() =>
                              setFormData((p) => ({ ...p, [field]: "" }))
                            }
                          >
                            ×
                          </button>
                        </div>
                        {/* Counter sits outside the flex row so it lines up under the field */}
                        <AdminCharCounter value={value} max={ADMIN_CLUB_COLOR_MAX_LENGTH} />
                        </>
                      ) : (
                        <button
                          type="button"
                          className="club-color-add"
                          onClick={() =>
                            setFormData((p) => ({ ...p, [field]: "#ffffff" }))
                          }
                        >
                          + {t("admin_club_color_add")}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Active toggle — only on edit */}
              {modalType === "edit" && (
                <div className="form-row">
                  <div className="form-group form-group--inline">
                    <label>{t("admin_active")}</label>
                    <button
                      type="button"
                      className={`active-toggle-btn ${formData.active ? "is-active" : "is-inactive"}`}
                      onClick={() =>
                        setFormData((p) => ({ ...p, active: !p.active }))
                      }
                    >
                      {formData.active ? (
                        <>
                          <FaToggleOn /> {t("admin_active")}
                        </>
                      ) : (
                        <>
                          <FaToggleOff /> {t("admin_inactive")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button
                className="modal-confirm-btn"
                onClick={modalType === "create" ? handleCreate : handleUpdate}
              >
                {modalType === "create"
                  ? t("admin_create_btn")
                  : t("admin_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {modalType === "delete" && selectedClub && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_club_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <div className="delete-club-info">
                <FaBuilding size={32} />
                <p>
                  <strong>{selectedClub.shortName}</strong>
                  {(selectedClub.nameTr || selectedClub.nameEn) && (
                    <span className="delete-club-longname">
                      {selectedClub.nameTr && ` · TR: ${selectedClub.nameTr}`}
                      {selectedClub.nameEn && ` · EN: ${selectedClub.nameEn}`}
                    </span>
                  )}
                  {selectedClub.longName && (
                    <span className="delete-club-longname">
                      {" "}
                      — {selectedClub.longName}
                    </span>
                  )}
                </p>
                {selectedClub.teams?.length > 0 && (
                  <div className="delete-entry-warning">
                    ⚠️{" "}
                    {t("admin_club_delete_teams_warning", {
                      count: selectedClub.teams.length,
                    })}
                  </div>
                )}
                <p className="delete-warning-text">
                  🗑️ {t("admin_confirm_delete_permanent")}
                </p>
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-delete-btn" onClick={handleDelete}>
                {t("admin_delete_btn")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Lightbox — full size logo preview */}
      {lightboxUrl && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxUrl}
            alt="Logo"
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        </div>
      )}

      {/* Lock-with-reason modal — only shown when locking, unlock stays one-click */}
      {lockTarget && (
        <ConfirmModal
          message={t("admin_club_lock_confirm")}
          confirmLabel={lockBusy ? "..." : t("admin_topic_lock")}
          cancelLabel={t("admin_cancel")}
          onConfirm={handleConfirmLock}
          onCancel={handleCancelLock}
          disabled={lockBusy}
        >
          <LockReasonFields
            reason={lockReasonDraft}
            onReasonChange={setLockReasonDraft}
            visible={lockReasonVisibleDraft}
            onVisibleChange={setLockReasonVisibleDraft}
          />
        </ConfirmModal>
      )}

      {lockInfoTarget && (
        <LockInfoModal
          lockedByUsername={lockInfoTarget.lockedByUsername}
          lockedAt={lockInfoTarget.lockedAt}
          lockReason={lockInfoTarget.lockReason}
          lockReasonVisible={lockInfoTarget.lockReasonVisible}
          onClose={() => setLockInfoTarget(null)}
        />
      )}
    </div>
  );
};